import React, { useState, useEffect, useRef } from "react";
import Spinner from "../../components/Tools/spinner";
import { useSelector } from 'react-redux';
import { BankSettlementMail } from "./../../helper/Api";
import DataTable from 'react-data-table-component';
import SubHeader from "../../components/Subheader/Subheader";
import CustomBreadcrumbs from "../../components/Tools/Breadcumbs";
import moment from "moment";
import { Card, CardContent, Chip, Tooltip } from '@mui/material';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import './BankMail.css';

const formatIndianAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '';
    return Number(amount).toLocaleString('en-IN');
};

const YesPendingCell = ({ value }) => {
    const isYes = String(value) === '1';
    return (
        <span className={`bank-mail-status-pill ${isYes ? 'bank-mail-status-pill--yes' : 'bank-mail-status-pill--pending'}`}>
            {isYes ? 'Yes' : 'Pending'}
        </span>
    );
};

const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 48;
const TABLE_PAGINATION_HEIGHT = 56;
const TABLE_BOTTOM_GAP = 24;

const BankMail = () => {
    const { financialYears } = useSelector((state) => state.authData);
    const fyIdRef = useRef(localStorage.getItem('globalDateId'));
    const tableContainerRef = useRef(null);
    const fyList = financialYears.payload.fy_list;
    const [loading, setLoading] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [bankMailRecords, setBankMailRecords] = useState([]);
    const [tableScrollHeight, setTableScrollHeight] = useState('400px');

    const customStyles = {
        headCells: {
            style: {
                fontWeight: "bold",
                fontSize: "14px",
                backgroundColor: "#f8f9fc",
            },
        },
        cells: {
            style: {
                fontSize: "13px",
            },
        },
    };

    const handlePageTrigger = () => {
        fyIdRef.current = localStorage.getItem('globalDateId');
    };

    const handleDownload = (url) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getStatusChip = (status) => {
        if (status === 'onhold') {
            return <Chip label="On Hold" color="warning" size="small" />;
        }
        if (status === 'completed') {
            return <Chip label="Sent to Bank" color="success" size="small" />;
        }
        if (status === 'settled') {
            return <Chip label="Completed" color="secondary" size="small" />;
        }
        return <Chip label="In Queue" color="primary" size="small" />;
    };

    const columns = [
        {
            name: 'Unique ID',
            selector: (row) => row.id,
            width: "150px",
        },
        {
            name: 'Sett. Date',
            selector: (row) => row.settlement_date
                ? moment(row.settlement_date, 'YYYY-MM-DD').format('DD-MM-YYYY')
                : '',
            width: "150px",
        },
        {
            name: 'Status',
            cell: (row) => getStatusChip(row.status),
            center: true,
            width: "130px",
        },
        {
            name: 'Amount',
            cell: (row) => <span className="bank-mail-amount">{formatIndianAmount(row.amount)}</span>,
            right: true,
            width: "130px",
        },
        {
            name: 'Account No',
            selector: (row) => row.account_no,
            wrap: true,
            minWidth: "150px",
        },
        {
            name: 'Mail Sent',
            cell: (row) => <YesPendingCell value={row.is_mail_sent} />,
            center: true,
            width: "110px",
        },
        {
            name: 'File Shared',
            cell: (row) => <YesPendingCell value={row.is_file_shared} />,
            center: true,
            width: "110px",
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="bank-mail-actions">
                    <Tooltip title="Download Mail Attachment" arrow placement="top">
                        <span className="bank-mail-action-tooltip-wrap">
                            <button
                                type="button"
                                className="bank-mail-action-btn bank-mail-action-btn--mail"
                                disabled={!row?.file_url}
                                onClick={() => handleDownload(row.file_url)}
                            >
                                <AiOutlineVerticalAlignBottom size={16} />
                            </button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Download SFTP File" arrow placement="top">
                        <span className="bank-mail-action-tooltip-wrap">
                            <button
                                type="button"
                                className="bank-mail-action-btn bank-mail-action-btn--sftp"
                                disabled={!row?.sftp_file_url}
                                onClick={() => handleDownload(row.sftp_file_url)}
                            >
                                <AiOutlineVerticalAlignBottom size={16} />
                            </button>
                        </span>
                    </Tooltip>
                </div>
            ),
            center: true,
            width: "110px",
        },
        {
            name: 'Created At',
            selector: (row) => row.created_at
                ? moment(row.created_at, 'YYYY-MM-DD HH:mm:ss').format('DD-MM-YYYY hh:mm A')
                : '',
            wrap: true,
            minWidth: "170px",
        },
    ];

    const handleChangeRowsPerPage = (currentRowsPerPage) => {
        setRowsPerPage(currentRowsPerPage);
    };

    const fetchBankSettlementMails = async () => {
        try {
            setLoading(true);
            const res = await BankSettlementMail();
            if (res.data.status) {
                setBankMailRecords(res.data.data);
            }
        } catch (error) {
            console.log("fetchBankSettlementMails error", error);
            setBankMailRecords([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBankSettlementMails();
    }, []);

    useEffect(() => {
        const updateTableHeight = () => {
            const rowCount = Math.min(bankMailRecords.length || 1, rowsPerPage);
            const bodyContentHeight = rowCount * TABLE_ROW_HEIGHT;
            const containerTop = tableContainerRef.current?.getBoundingClientRect().top ?? 220;
            const maxBodyHeight = window.innerHeight
                - containerTop
                - TABLE_HEADER_HEIGHT
                - TABLE_PAGINATION_HEIGHT
                - TABLE_BOTTOM_GAP;
            const nextHeight = Math.min(
                bodyContentHeight,
                Math.max(maxBodyHeight, 120)
            );
            setTableScrollHeight(`${nextHeight}px`);
        };

        updateTableHeight();
        window.addEventListener('resize', updateTableHeight);
        return () => window.removeEventListener('resize', updateTableHeight);
    }, [bankMailRecords.length, rowsPerPage, loading]);

    return (
        <div className="bank-mail-page">
            {loading && <div><Spinner /></div>}
            <SubHeader fy_list={fyList} handlePageTrigger={handlePageTrigger} />
            <div className="row" style={{ padding: "10px 16px" }}>
                <div className="col-md-6">
                    <h4 style={{ color: "#28326f", margin: 0 }}>Bank Settlement Mail Report</h4>
                    <CustomBreadcrumbs linksArray={[]} title="Bank Settlement Mail Report" />
                </div>
            </div>
            <div ref={tableContainerRef}>
            <Card className="bank-mail-table-card">
                <CardContent>
                    <DataTable
                        customStyles={customStyles}
                        className="bank-mail-data-table"
                        columns={columns}
                        data={bankMailRecords}
                        sortable={false}
                        fixedHeader
                        fixedHeaderScrollHeight={tableScrollHeight}
                        pagination
                        paginationPerPage={rowsPerPage}
                        paginationTotalRows={bankMailRecords.length}
                        paginationRowsPerPageOptions={[100, 200, 500, 1000]}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </CardContent>
            </Card>
            </div>
        </div>
    );
};

export default BankMail;
